/**
 * The affected area: the run's own maximum-depth raster on the shared satellite
 * basemap, with every settlement marked on it.
 *
 * A settlement's colour is its own CAP severity and its radius is its
 * population, so the map says at a glance where the deep water is and how many
 * people are under each severity. Clicking one filters the whole page to it;
 * clicking it again clears the filter.
 *
 * The raster is the overlay PNG the results API renders for this run, placed by
 * the bounds the same call returns - the settlements and the raster are in the
 * same coordinate frame by construction. The map fits those bounds.
 */

import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Marker } from 'react-map-gl/maplibre'
import type { LayerSpecification, SourceSpecification } from 'maplibre-gl'

import { api, assetUrl } from '../../api/client'
import type { Overlay } from '../../api/types'
import { BaseMap, type Bounds } from '../../components/BaseMap'
import { SEVERITY_COLOUR } from './shared'
import type { SettlementRow } from './shared'

interface Props {
  runId: string
  settlements: SettlementRow[]
  selected: SettlementRow | null
  onSelect: (settlement: SettlementRow | null) => void
}

const HEIGHT = 360

/** Radius in px for a population, on a square-root scale so area tracks people. */
function radius(population: number | null): number {
  if (!population) return 5
  return Math.max(5, Math.min(20, Math.sqrt(population) / 18))
}

export function AffectedAreaMap({ runId, settlements, selected, onSelect }: Props) {
  const overlay = useQuery<Overlay>({
    queryKey: ['alerts-overlay', runId],
    queryFn: () => api.overlay(runId, 'max_depth'),
  })
  const map = overlay.data
  const boundsKey = map?.bounds.join(',') ?? ''

  const data = useMemo(() => {
    if (!map) return null
    const [w, s, e, n] = map.bounds
    const sources: Record<string, SourceSpecification> = {
      depth: {
        type: 'image',
        url: assetUrl(map.png_url),
        coordinates: [
          [w, n],
          [e, n],
          [e, s],
          [w, s],
        ],
      },
    }
    const layers: LayerSpecification[] = [
      { id: 'depth-layer', type: 'raster', source: 'depth', paint: { 'raster-fade-duration': 0 } },
    ]
    return { sources, layers }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [boundsKey, map?.png_url])

  if (!map || !data) return <div className="skeleton" style={{ height: HEIGHT }} />

  return (
    <section className="panel" style={{ padding: 'var(--s3)' }} data-testid="affected-area-map">
      <div className="mb-2 flex items-baseline justify-between gap-2">
        <span className="section-label">Affected area</span>
        {selected && (
          <button type="button" className="ctl" onClick={() => onSelect(null)}>
            Clear filter
          </button>
        )}
      </div>
      <div style={{ height: HEIGHT, borderRadius: 'var(--r-control)', overflow: 'hidden' }}>
        <BaseMap bounds={map.bounds as Bounds} runId={runId} sources={data.sources} layers={data.layers} padding={24} dragRotate={false} maxPitch={0}>
          {settlements.map((s) => {
            const colour = SEVERITY_COLOUR[s.alert.severity ?? 'Minor']
            const active = selected?.settlement_id === s.settlement_id
            const r = radius(s.population)
            return (
              <Marker key={s.settlement_id} longitude={s.lon} latitude={s.lat} anchor="center" style={{ zIndex: active ? 3 : 2 }}>
                <button
                  type="button"
                  onClick={() => onSelect(active ? null : s)}
                  className="relative block cursor-pointer"
                  style={{ width: 2 * r, height: 2 * r }}
                  aria-label={s.name}
                >
                  <span
                    className="absolute inset-0 rounded-full"
                    style={{ background: colour, opacity: active ? 0.85 : 0.45, border: `${active ? 2.5 : 1.5}px solid ${colour}` }}
                  />
                  <span
                    className="pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-[4px] bg-slate-950/85 px-1 text-12"
                    style={{ left: 2 * r + 5, color: active ? 'var(--ink-text)' : 'var(--ink-text-dim)' }}
                  >
                    {s.name}
                  </span>
                </button>
              </Marker>
            )
          })}
        </BaseMap>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
        {settlements.map((s) => (
          <span
            key={s.settlement_id}
            className="flex items-center gap-1.5"
            style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-dim)' }}
          >
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: 9,
                background: SEVERITY_COLOUR[s.alert.severity ?? 'Minor'],
              }}
            />
            {s.name}
            <span className="font-mono" style={{ color: 'var(--ink-text-faint)' }}>
              {(s.population ?? 0).toLocaleString('en-IN')}
            </span>
          </span>
        ))}
      </div>
      <div
        className="mt-2 font-mono"
        style={{ fontSize: 'var(--fs-label)', color: 'var(--ink-text-faint)' }}
      >
        maximum depth 0 – {map.vmax.toFixed(2)} m · marker area ∝ population
      </div>
    </section>
  )
}
